import Header from "@/components/Header";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Clock, ShieldCheck, Star, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TIDS } from "@/constants/testIds";

const COMPANY_PHONE = "+64225250123";
const HERO_IMG = "https://images.unsplash.com/photo-1655827763440-7905302b75ff?crop=entropy&cs=srgb&fm=jpg&q=85";
const SUV_IMG = "https://images.unsplash.com/photo-1707407772603-274cc5cf18f4?crop=entropy&cs=srgb&fm=jpg&q=85";
const CHAUFFEUR_IMG = "https://images.unsplash.com/photo-1627285886624-5cd637dafb50?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#090D14] text-white">
      <Header />

      {/* HERO */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-amber-500/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="text-caption mb-4">Auckland · Wellington · Christchurch · Queenstown</div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white font-display">
                Chauffeured comfort,<br />
                <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">on the clock, always.</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg text-slate-400 leading-relaxed">
                Book a ride in under 30 seconds — airport transfers, corporate runs, or a night out.
                Vetted drivers, premium fleet, transparent NZ fares.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/book" data-testid={TIDS.hero.ctaBook}>
                  <Button size="lg" className="btn-gold rounded-full h-12 px-7 text-base">
                    Book a ride <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a href={`tel:${COMPANY_PHONE}`} data-testid={TIDS.hero.ctaCall}>
                  <Button size="lg" variant="outline" className="rounded-full h-12 px-6 bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-amber-400">
                    <Phone className="mr-2 w-4 h-4" /> {COMPANY_PHONE}
                  </Button>
                </a>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
                {[
                  { v: "24/7", l: "Dispatch" },
                  { v: "4.9★", l: "Rider score" },
                  { v: "< 8m", l: "Avg. pickup" },
                ].map((s, i) => (
                  <div key={s.l} className={`fade-up fade-up-${i + 1}`}>
                    <div className="font-display text-3xl font-extrabold text-amber-400">{s.v}</div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 fade-up fade-up-2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/30 to-transparent rounded-3xl blur-2xl -z-10" />
                <div className="relative rounded-3xl overflow-hidden border border-amber-400/20 shadow-2xl">
                  <img src={HERO_IMG} alt="Executive vehicle" className="w-full h-[420px] object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#090D14] via-transparent to-transparent" />
                </div>
                <div className="absolute -bottom-6 -left-6 glass-card rounded-2xl p-4 gold-border max-w-[260px]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-400/20 grid place-items-center">
                      <Sparkles className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-400">Estimate</div>
                      <div className="font-display font-bold text-lg">NZ$ 42.50</div>
                    </div>
                  </div>
                  <div className="mt-2 h-1 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full w-3/4 bg-gradient-to-r from-amber-400 to-amber-600" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-caption mb-3">Frictionless</div>
          <h2 className="text-3xl sm:text-4xl font-bold font-display max-w-2xl">Three ways to grab a car.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[
              { icon: MapPin, t: "Book online", d: "Fill the form. Confirmation and reference number sent instantly." },
              { icon: Phone, t: "Call dispatch", d: `Ring ${COMPANY_PHONE} — manual bookings handled 24/7.` },
              { icon: Clock, t: "Scan the QR", d: "Point-camera-book. Perfect for hotel lobbies and events." },
            ].map((s, i) => (
              <div key={s.t} className={`glass-card rounded-2xl p-6 gold-border fade-up fade-up-${i + 1}`}>
                <div className="w-11 h-11 rounded-lg bg-amber-400/10 grid place-items-center mb-4">
                  <s.icon className="w-5 h-5 text-amber-400" />
                </div>
                <div className="font-display text-xl font-semibold">{s.t}</div>
                <p className="text-slate-400 text-sm mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FLEET */}
      <section id="fleet" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-caption mb-3">The Fleet</div>
              <h2 className="text-3xl sm:text-4xl font-bold font-display">Built for every kind of trip.</h2>
            </div>
            <Link to="/book" className="hidden sm:block text-sm text-amber-400 hover:text-amber-300 font-medium">Book now →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { img: HERO_IMG, name: "Standard Sedan", price: "From NZ$ 8.50", pax: "1–4 pax" },
              { img: SUV_IMG, name: "Executive SUV", price: "From NZ$ 15.00", pax: "1–6 pax" },
              { img: CHAUFFEUR_IMG, name: "Airport Transfer", price: "Fixed NZ$ 55.00", pax: "1–4 pax" },
            ].map((c, i) => (
              <div key={c.name} className={`group relative rounded-2xl overflow-hidden border border-white/10 hover:border-amber-400/40 transition-colors fade-up fade-up-${i + 1}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={c.img} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#090D14] via-[#090D14]/70 to-transparent p-5">
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="font-display text-lg font-semibold">{c.name}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{c.pax}</div>
                    </div>
                    <div className="text-amber-400 font-semibold text-sm">{c.price}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-caption mb-3">Trust & safety</div>
            <h2 className="text-3xl sm:text-4xl font-bold font-display">Vetted drivers. Encrypted payments. Zero surprises.</h2>
            <ul className="mt-6 space-y-4 text-slate-300">
              {[
                "Background-checked chauffeurs with NZTA endorsements.",
                "PCI-DSS-compliant Stripe checkout — card data never touches our servers.",
                "Every admin action is audit-logged with IP and timestamp.",
              ].map((t) => (
                <li key={t} className="flex gap-3">
                  <ShieldCheck className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="glass-card rounded-xl p-5 gold-border">
                <Star className="w-4 h-4 text-amber-400 mb-3" />
                <p className="text-sm text-slate-300 leading-relaxed">"On time, spotless car, courteous driver. Booked twice this week already."</p>
                <div className="mt-3 text-xs text-slate-500 font-mono uppercase tracking-wider">— Rider · Auckland</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT / CTA */}
      <section id="contact" className="py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="text-caption mb-4">Ready when you are</div>
          <h2 className="text-4xl sm:text-5xl font-bold font-display max-w-2xl mx-auto leading-tight">
            Somewhere to be? <span className="text-amber-400">Let's move.</span>
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/book"><Button size="lg" className="btn-gold rounded-full h-12 px-7">Book online</Button></Link>
            <a href={`tel:${COMPANY_PHONE}`}>
              <Button size="lg" variant="outline" className="rounded-full h-12 px-6 bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-amber-400">
                <Phone className="mr-2 w-4 h-4" /> {COMPANY_PHONE}
              </Button>
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-xs text-slate-500" data-testid="landing-footer">
        <div className="flex items-center justify-center gap-4 mb-2 flex-wrap">
          <Link to="/privacy" className="hover:text-red-300 transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
          <span className="text-slate-700">·</span>
          <Link to="/terms" className="hover:text-red-300 transition-colors" data-testid="footer-terms">Terms & Conditions</Link>
          <span className="text-slate-700">·</span>
          <Link to="/contact" className="hover:text-red-300 transition-colors" data-testid="footer-contact">Contact</Link>
        </div>
        © TRIPTAP 2026. All Rights Reserved.
      </footer>
    </div>
  );
}
